import React, { useState } from 'react';
import produce from 'immer';
import { BucketKind } from '../../../../../../utils/apollo/graphqlSchema';
import * as BucketsUI from '../BucketsUI';
import * as RangeBucketsUI from './RangeBucketsUI';
import {
  setBreakdownGroupBinning,
  setSelectedBinning,
} from '../../../../../../redux/slices/oldBuilder';
import { IRangeBinning } from '../../../../../../redux/slices/oldBuilder/binning/types';
import { useAppDispatch } from '../../../../../../redux/hooks';
import { BucketRowControlGroups } from '../../components';
import { MAX_INTEGER } from '../../../../../../config/constants';
import {
  BINNING_PRECISION,
  checkRangeBucketsOverlapAndError,
} from '../../utils';
import { MenuActions } from '../../types';
import { isNil } from 'lodash';
import { defaultRangeBucket } from '../../../../../../redux/slices/oldBuilder/binning/defaults';

enum MinMaxEnum {
  MIN = 'min',
  MAX = 'max',
}

interface IProps {
  binning: IRangeBinning;
  groupIndex?: number;
}

export default ({ binning, groupIndex }: IProps) => {
  const dispatch = useAppDispatch();

  const customBuckets = binning.customBuckets;

  const [renameEditorIndex, setRenameEditorIndex] = useState(-1);

  const overlaps = checkRangeBucketsOverlapAndError(customBuckets);

  const onRangeBracketChange = (
    newValue: number | null,
    index: number,
    minmax: MinMaxEnum,
  ) => {
    const newBinning = produce(binning, (newBinning) => {
      newBinning.customBuckets[index] = {
        ...newBinning.customBuckets[index],
        kind: BucketKind.Range,
        // Number('') will give the result of number 0 which is not correct.
        [minmax]: newValue,
      };
      delete newBinning.namedBucketDef;
    });

    if (isNil(groupIndex)) {
      dispatch(
        setSelectedBinning({
          newBinning,
        }),
      );
    } else {
      dispatch(
        setBreakdownGroupBinning({
          groupIndex,
          newBinning,
        }),
      );
    }
  };

  const onNameChangeHandler = (newName: string, index: number) => {
    const newBinning = produce(binning, (newBinning) => {
      newBinning.customBuckets[index].name = newName;
      newBinning.customBuckets[index].isCustomName = newName.length > 0;
      delete newBinning.namedBucketDef;
    });

    if (isNil(groupIndex)) {
      dispatch(
        setSelectedBinning({
          newBinning,
        }),
      );
    } else {
      dispatch(
        setBreakdownGroupBinning({
          groupIndex,
          newBinning,
        }),
      );
    }

    setRenameEditorIndex(-1);
  };

  const bucketRowActionsHandler = (action: MenuActions, index: number) => {
    if (action === MenuActions.Edit) {
      setRenameEditorIndex(index);
      return;
    }
    const newBinning = produce(binning, (newBinning) => {
      if (action === MenuActions.Add) {
        // insert new empty bucket on the next index.
        newBinning.customBuckets.splice(index + 1, 0, defaultRangeBucket);
      } else if (action === MenuActions.Remove) {
        // remove selected bucket
        newBinning.customBuckets.splice(index, 1);
      }
      delete newBinning.namedBucketDef;
    });

    if (isNil(groupIndex)) {
      dispatch(
        setSelectedBinning({
          newBinning,
        }),
      );
    } else {
      dispatch(
        setBreakdownGroupBinning({
          groupIndex,
          newBinning,
        }),
      );
    }
  };

  return (
    <BucketsUI.BucketsContainer>
      {customBuckets.map((bucket, index) => (
        <BucketsUI.BracketContainer key={'RangeBracket-' + index}>
          <RangeBucketsUI.ManualRangeBracket>
            <BucketsUI.Text>{`${index + 1}.`}</BucketsUI.Text>
            <RangeBucketsUI.ManualRangeNumberInput
              tabIndex={1}
              placeholder="Min"
              onChange={(newValue: number | null) =>
                onRangeBracketChange(newValue, index, MinMaxEnum.MIN)
              }
              value={bucket.min}
              hasError={
                overlaps[index] ||
                (!isNil(bucket.min) && bucket.min > MAX_INTEGER)
              }
              errorMessage={
                overlaps[index]
                  ? `Overlap error`
                  : `Bin value exceeds the maximum integer`
              }
              precision={BINNING_PRECISION}
            />
            <BucketsUI.Text>to</BucketsUI.Text>
            <RangeBucketsUI.ManualRangeNumberInput
              tabIndex={1}
              placeholder="Max"
              onChange={(newValue: number | null) =>
                onRangeBracketChange(newValue, index, MinMaxEnum.MAX)
              }
              value={bucket.max}
              hasError={
                overlaps[index] ||
                (!isNil(bucket.max) && bucket.max > MAX_INTEGER)
              }
              errorMessage={
                overlaps[index]
                  ? `Overlap error`
                  : `Bin value exceeds the maximum integer`
              }
              precision={BINNING_PRECISION}
            />
            <BucketRowControlGroups
              bucketRowActionsHandler={bucketRowActionsHandler}
              isOpenEditWindow={renameEditorIndex === index}
              rowIndex={index}
              bucketsLength={customBuckets.length}
              onNameChangeHandler={onNameChangeHandler}
              customName={
                bucket.isCustomName && bucket.name ? bucket.name : undefined
              }
            />
          </RangeBucketsUI.ManualRangeBracket>
          {overlaps[index] ? (
            <RangeBucketsUI.BinError>
              Invalid input or overlapped bins
            </RangeBucketsUI.BinError>
          ) : bucket.name && bucket.isCustomName ? (
            <BucketsUI.CustomName>{bucket.name}</BucketsUI.CustomName>
          ) : null}
        </BucketsUI.BracketContainer>
      ))}
    </BucketsUI.BucketsContainer>
  );
};
